<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const source = `// 经典二分查找：从有序数组中寻找目标
function binarySearch(numbers, target) {
  let left = 0;
  let right = numbers.length - 1;

  while (left <= right) {
    const middle = Math.floor((left + right) / 2);
    if (numbers[middle] === target) return middle;
    if (numbers[middle] < target) left = middle + 1;
    else right = middle - 1;
  }

  return -1;
}

binarySearch([2, 5, 8, 12, 16], 12); // 3`

const output = ref('')
const status = ref('AI 正在生成')
const tokenize = line => {
  const pattern = /(\/\/.*$)|("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*')|(\b(?:function|let|const|while|if|else|return|true|false|null)\b)|(\b(?:Math|Array|String|Number|console)\b)|(\b(?:length|floor|log)\b)|(\b\d+\b)|([=!<>+\-*/]+)|([()[\]{},;.])/g
  const tokens = []
  let cursor = 0, match
  while ((match = pattern.exec(line)) !== null) {
    if (match.index > cursor) tokens.push({ text: line.slice(cursor, match.index), type: 'plain' })
    const type = match[1] ? 'comment' : match[2] ? 'string' : match[3] ? 'keyword' : match[4] ? 'builtin' : match[5] ? 'property' : match[6] ? 'number' : match[7] ? 'operator' : 'punctuation'
    tokens.push({ text: match[0], type })
    cursor = pattern.lastIndex
  }
  if (cursor < line.length) tokens.push({ text: line.slice(cursor), type: 'plain' })
  return tokens
}
const lines = computed(() => output.value.split('\n').map(line => tokenize(line)))
let timeoutId

const wait = delay => new Promise(resolve => { timeoutId = window.setTimeout(resolve, delay) })
const streamCode = async () => {
  while (true) {
    output.value = ''
    status.value = 'AI 正在生成'
    for (let index = 0; index < source.length; index += 1) {
      output.value += source[index]
      await wait(source[index] === '\n' ? 55 : 18 + Math.random() * 18)
    }
    status.value = '生成完成'
    await wait(2600)
  }
}

onMounted(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    output.value = source
    status.value = '生成完成'
  } else streamCode()
})
onBeforeUnmount(() => window.clearTimeout(timeoutId))
</script>

<template>
  <div class="beginner-page">
    <section class="beginner-hero">
      <div class="beginner-copy">
        <span class="signal"><i></i> AI 编程 · 零基础友好</span>
        <h1>写代码的门槛<br><strong>正在降低</strong></h1>
        <p>不需要先背完所有语法。从一个真实的小目标开始，让 AI 解释、生成、调试，再由你理解每一步为什么有效。</p>
        <RouterLink class="button" to="/agent-guide">开始学习 AI 编程</RouterLink>
      </div>
      <div class="code-window" aria-label="AI 流式生成二分查找代码示例">
        <header><div class="window-dots"><i></i><i></i><i></i></div><span>binary-search.js</span><b :class="{ done: status === '生成完成' }"><i></i>{{ status }}</b></header>
        <div class="code-body"><div v-for="(line,index) in lines" :key="index" class="code-line"><span>{{ index + 1 }}</span><code><template v-for="(token,tokenIndex) in line" :key="tokenIndex"><em :class="`token-${token.type}`">{{ token.text }}</em></template><i v-if="index === lines.length - 1 && status !== '生成完成'" class="typing-caret"></i></code></div></div>
        <footer><span>◷ JavaScript</span><span>UTF-8</span></footer>
      </div>
    </section>

    <section class="beginner-promise"><span>不要求编程经验</span><span>每一步都有解释</span><span>代码可以直接运行</span><span>从小作品建立信心</span></section>

    <section id="learning-path" class="learning-path">
      <div class="learning-heading"><span>从哪里开始</span><h2>先完成，再理解，再独立修改</h2><p>零基础学习最重要的不是一次记住，而是建立持续获得正确反馈的循环。</p></div>
      <ol><li><b>01</b><div><h3>描述你想做什么</h3><p>把模糊想法整理成一个可以运行的小功能。</p></div></li><li><b>02</b><div><h3>看懂 AI 生成的代码</h3><p>逐行解释变量、判断和函数，不跳过关键概念。</p></div></li><li><b>03</b><div><h3>亲手修改并运行</h3><p>改变输入、样式和逻辑，让代码真正成为你的作品。</p></div></li></ol>
    </section>
  </div>
</template>
